/**
 * QC report builder (cue-forge-n3e.6). A QC run is the billable proof-of-work a
 * vendor hands to a client, so this turns a run into a self-describing artifact:
 * a CSV (spreadsheet-friendly) and a printable HTML page, each carrying the
 * preset name, the actual thresholds that were applied, summary counts, and one
 * row per issue (cue, timecode, rule, severity, message, fixed/remaining).
 *
 * Pure and DOM-free so it is unit-testable; the QcProvider hands the strings to
 * the shared `session/download.ts` helper. "Fixed vs remaining" is a diff of the
 * run's BASELINE issues against the current list, using the same content
 * identity (ruleId + cueIndex + message) the cursor reconciler trusts — an issue
 * the operator has since cleared is no longer in the current list, so it reads
 * as fixed.
 */

import type { QCIssue, Ruleset, Severity, SubtitleDocument } from '../../core';
import { formatTimecode } from '../editor/metrics';
import { acceptKey } from './accept';
import { ruleLabel } from './ruleLabels';
import {
  thresholdLines,
  verificationStatus,
  type ThresholdLine,
  type VerificationStatus,
} from './thresholds';

export type { ThresholdLine } from './thresholds';

export type ReportKind = 'csv' | 'html';
export type RowStatus = 'remaining' | 'fixed' | 'accepted';

export interface ReportInput {
  /** Original source file name, e.g. "movie.srt" — used to derive the report name. */
  sourceName: string;
  /** Human preset label shown to the user (e.g. "Generic"). */
  presetName: string;
  /** Preset id key (e.g. "generic"), printed alongside the label. */
  presetId: string;
  /** The active ruleset, so the header can print the actual thresholds applied. */
  ruleset: Ruleset;
  /** The current document, for resolving each remaining issue's cue timecode. */
  doc: SubtitleDocument;
  /**
   * The document as it was when QC ran. Fixed issues reference cues that may
   * have since moved or been deleted (e.g. empty-cue removal renumbers), so
   * their timecodes resolve against this snapshot, not the live doc.
   */
  baselineDoc: SubtitleDocument;
  /** Current (remaining) active issues. */
  issues: QCIssue[];
  /**
   * Issues the operator accepted as intentional (cue-forge-n3e.7). They are
   * present in the baseline but excluded from `issues`; without this category the
   * diff would mislabel them as "fixed". Resolved against the live doc. Defaults
   * to none, so callers that predate the feature are unaffected.
   */
  accepted?: QCIssue[];
  /** Issues captured when QC was run, for the fixed-vs-remaining diff. */
  baseline: QCIssue[];
  /** Generation time, injected so the builder stays pure/testable. */
  generatedAt: Date;
}

export interface ReportRow {
  cueIndex: number;
  timecode: string;
  ruleId: string;
  ruleLabel: string;
  severity: Severity;
  message: string;
  status: RowStatus;
  fixable: boolean;
}

export interface ReportModel {
  sourceName: string;
  presetName: string;
  presetId: string;
  generatedAtIso: string;
  generatedAtDisplay: string;
  cueCount: number;
  /** Whether the preset's thresholds are verified against the cited spec (n3e.8). */
  verification: VerificationStatus;
  thresholds: ThresholdLine[];
  summary: {
    /** Issues the run found = remaining + accepted + fixed (equals rows.length). */
    found: number;
    /** Errors across every row, so it matches the table. */
    errors: number;
    warnings: number;
    /** Auto-fixable issues among those that REMAIN (the actionable count). */
    fixable: number;
    fixed: number;
    remaining: number;
    /** Issues the operator marked intentional (cue-forge-n3e.7). */
    accepted: number;
  };
  /** Remaining issues first (engine order), then accepted, then fixed-since-run. */
  rows: ReportRow[];
}

/** Content identity of an issue — matches the cursor reconciler's notion. */
function contentKey(i: QCIssue): string {
  return `${i.ruleId} ${i.cueIndex} ${i.message}`;
}

/** ISO display without the fractional seconds, tagged UTC (deterministic). */
function displayTime(iso: string): string {
  return iso.replace('T', ' ').replace(/\.\d+Z$/, ' UTC');
}

function toRow(issue: QCIssue, status: RowStatus, startByIndex: Map<number, number>): ReportRow {
  const startMs = issue.cueIndex >= 0 ? startByIndex.get(issue.cueIndex) : undefined;
  return {
    cueIndex: issue.cueIndex,
    timecode: startMs === undefined ? '' : formatTimecode(startMs),
    ruleId: issue.ruleId,
    ruleLabel: ruleLabel(issue.ruleId),
    severity: issue.severity,
    message: issue.message,
    status,
    fixable: issue.fixable,
  };
}

export function buildReportModel(input: ReportInput): ReportModel {
  const { issues, baseline, doc, baselineDoc } = input;
  const accepted = input.accepted ?? [];
  const startNow = new Map(doc.cues.map((c) => [c.index, c.startMs]));
  const startAtRun = new Map(baselineDoc.cues.map((c) => [c.index, c.startMs]));

  // A baseline issue the operator has since accepted is present-but-set-aside,
  // NEVER fixed. Match it on the ACCEPT identity (the content hash, which
  // survives renumbering and metric drift) rather than the contentKey (which
  // embeds the cueIndex + measured-value message and so drifts) — otherwise a
  // renumbered/retimed accepted issue's frozen baseline twin is mislabeled
  // 'fixed' and double-counted alongside its 'accepted' row (cue-forge-n3e.7).
  const acceptedKeys = new Set(accepted.map((a) => acceptKey(a, doc)));
  // Multiset diff for the rest: a baseline issue is "fixed" only when no live
  // ACTIVE issue still carries its content key (decrement per match, so two
  // identical issues fixed down to one report one fixed, not zero).
  const liveCounts = new Map<string, number>();
  for (const i of issues) {
    const k = contentKey(i);
    liveCounts.set(k, (liveCounts.get(k) ?? 0) + 1);
  }
  const fixed = baseline.filter((b) => {
    if (acceptedKeys.has(acceptKey(b, baselineDoc))) return false; // accepted, not fixed
    const k = contentKey(b);
    const c = liveCounts.get(k) ?? 0;
    if (c > 0) {
      liveCounts.set(k, c - 1);
      return false;
    }
    return true;
  });

  // Remaining + accepted rows resolve against the live doc (their cues still
  // exist); fixed rows against the run snapshot (their cue may have moved/gone).
  const rows: ReportRow[] = [
    ...issues.map((i) => toRow(i, 'remaining', startNow)),
    ...accepted.map((i) => toRow(i, 'accepted', startNow)),
    ...fixed.map((i) => toRow(i, 'fixed', startAtRun)),
  ];

  // Counts over the full row set so the header reconciles with the table.
  const errors = rows.filter((r) => r.severity === 'error').length;
  const fixable = issues.filter((i) => i.fixable).length;
  const iso = input.generatedAt.toISOString();

  return {
    sourceName: input.sourceName,
    presetName: input.presetName,
    presetId: input.presetId,
    generatedAtIso: iso,
    generatedAtDisplay: displayTime(iso),
    cueCount: doc.cues.length,
    verification: verificationStatus(input.ruleset),
    thresholds: thresholdLines(input.ruleset),
    summary: {
      found: rows.length,
      errors,
      warnings: rows.length - errors,
      fixable,
      fixed: fixed.length,
      remaining: issues.length,
      accepted: accepted.length,
    },
    rows,
  };
}

// ---------------------------------------------------------------------------
// CSV
// ---------------------------------------------------------------------------

/**
 * One RFC-4180 CSV cell. Also neutralizes spreadsheet formula injection: a cell
 * beginning with `= + - @` (or a control char) is prefixed with an apostrophe so
 * Excel/Sheets treat it as literal text rather than executing it.
 */
function csvCell(value: string | number): string {
  let s = String(value);
  // Leading formula char, or leading whitespace then a formula char (which some
  // importers trim before parsing) — both get the neutralizing apostrophe.
  const dangerous = /^[=+\-@\t\r]/.test(s) || /^\s+[=+\-@]/.test(s);
  if (dangerous) s = `'${s}`;
  // Quote when RFC-4180 requires it, and always quote a formula-guarded cell so
  // the neutralizing apostrophe is unambiguous to every importer.
  if (dangerous || /[",\n\r]/.test(s)) s = `"${s.replace(/"/g, '""')}"`;
  return s;
}

function csvRow(cells: (string | number)[]): string {
  return cells.map(csvCell).join(',');
}

export function reportToCsv(model: ReportModel): string {
  const lines: string[] = [];
  lines.push(csvRow(['CueForge QC Report']));
  lines.push(csvRow(['Source', model.sourceName]));
  lines.push(csvRow(['Preset', `${model.presetName} (${model.presetId})`]));
  lines.push(csvRow(['Preset status', model.verification.label]));
  if (model.verification.reference) {
    lines.push(csvRow(['Preset reference', model.verification.reference]));
  }
  lines.push(csvRow(['Generated', model.generatedAtDisplay]));
  lines.push(csvRow(['Cues', model.cueCount]));
  lines.push(csvRow(['Issues found', model.summary.found]));
  lines.push(csvRow(['Errors', model.summary.errors]));
  lines.push(csvRow(['Warnings', model.summary.warnings]));
  lines.push(csvRow(['Auto-fixable (remaining)', model.summary.fixable]));
  lines.push(csvRow(['Fixed since run', model.summary.fixed]));
  lines.push(csvRow(['Accepted (intentional)', model.summary.accepted]));
  lines.push(csvRow(['Remaining', model.summary.remaining]));
  lines.push('');
  lines.push(csvRow(['Thresholds applied']));
  lines.push(csvRow(['Rule', 'Limit', 'Severity']));
  for (const t of model.thresholds) lines.push(csvRow([t.label, t.limit, t.severity]));
  lines.push('');
  lines.push(csvRow(['Issues']));
  lines.push('Cue,Timecode,Rule,Severity,Message,Status,Auto-fixable');
  for (const r of model.rows) {
    lines.push(
      csvRow([
        r.cueIndex >= 0 ? r.cueIndex : '',
        r.timecode,
        r.ruleLabel,
        r.severity,
        r.message,
        r.status,
        r.fixable ? 'yes' : 'no',
      ]),
    );
  }
  return lines.join('\r\n');
}

// ---------------------------------------------------------------------------
// HTML
// ---------------------------------------------------------------------------

function escapeHtml(value: string | number): string {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Whether a reference URL uses an http/https scheme (the only linkable ones, §7). */
function isHttpUrl(url: string): boolean {
  try {
    const { protocol } = new URL(url);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}

export function reportToHtml(model: ReportModel): string {
  const s = model.summary;
  const meta = [
    ['Source', model.sourceName],
    ['Preset', `${model.presetName} (${model.presetId})`],
    ['Preset status', model.verification.label],
    ['Generated', model.generatedAtDisplay],
    ['Cues', String(model.cueCount)],
    ['Issues found', String(s.found)],
    ['Errors', String(s.errors)],
    ['Warnings', String(s.warnings)],
    ['Auto-fixable (remaining)', String(s.fixable)],
    ['Fixed since run', String(s.fixed)],
    ['Accepted (intentional)', String(s.accepted)],
    ['Remaining', String(s.remaining)],
  ]
    .map(([k, v]) => `<tr><th>${escapeHtml(k!)}</th><td>${escapeHtml(v!)}</td></tr>`)
    .join('');

  // The preset reference as a clickable link. A custom/4o9-imported preset can
  // carry an attacker-controlled reference, so the scheme is allowlisted to
  // http/https here — escapeHtml neutralizes the value but NOT a `javascript:`
  // scheme, which would otherwise hand the client a click-to-run link (spec §7).
  // A non-http reference still prints as plain, non-linked text.
  const reference = model.verification.reference;
  const linkableReference = reference && isHttpUrl(reference) ? reference : undefined;
  const referenceRow = reference
    ? `<tr><th>Preset reference</th><td>${
        linkableReference
          ? `<a href="${escapeHtml(linkableReference)}">${escapeHtml(reference)}</a>`
          : escapeHtml(reference)
      }</td></tr>`
    : '';

  const thresholds = model.thresholds
    .map(
      (t) =>
        `<tr><td>${escapeHtml(t.label)}</td><td>${escapeHtml(t.limit)}</td><td>${escapeHtml(
          t.severity,
        )}</td></tr>`,
    )
    .join('');

  const rows = model.rows
    .map(
      (r) =>
        `<tr class="sev-${escapeHtml(r.severity)} status-${escapeHtml(r.status)}">` +
        `<td class="num">${r.cueIndex >= 0 ? escapeHtml(r.cueIndex) : ''}</td>` +
        `<td class="mono">${escapeHtml(r.timecode)}</td>` +
        `<td>${escapeHtml(r.ruleLabel)}</td>` +
        `<td>${escapeHtml(r.severity)}</td>` +
        `<td>${escapeHtml(r.message)}</td>` +
        `<td>${escapeHtml(r.status)}</td>` +
        `<td>${r.fixable ? 'yes' : 'no'}</td>` +
        `</tr>`,
    )
    .join('');

  const emptyNote =
    model.rows.length === 0 ? '<p class="clean">No issues found — clean pass.</p>' : '';

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>QC report — ${escapeHtml(model.sourceName)}</title>
<style>
  :root { color-scheme: light; }
  body { font: 14px/1.5 system-ui, -apple-system, Segoe UI, Roboto, sans-serif; color: #1a1a1a; margin: 2rem; }
  h1 { font-size: 1.4rem; margin: 0 0 0.25rem; }
  h2 { font-size: 1rem; margin: 1.5rem 0 0.5rem; }
  .sub { color: #555; margin: 0 0 1rem; }
  table { border-collapse: collapse; width: 100%; margin: 0.5rem 0; }
  th, td { border: 1px solid #ccc; padding: 4px 8px; text-align: left; vertical-align: top; }
  thead th { background: #f3f3f3; }
  .meta th { width: 12rem; background: #fafafa; }
  .mono, .num { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; white-space: nowrap; }
  .num { text-align: right; }
  tr.sev-error td:nth-child(4) { color: #b00020; font-weight: 600; }
  tr.status-fixed { color: #6b7280; }
  tr.status-fixed td:nth-child(6) { color: #047857; font-weight: 600; }
  tr.status-accepted td:nth-child(6) { color: #92400e; font-weight: 600; }
  .clean { font-weight: 600; color: #047857; }
  @media print { body { margin: 0.5cm; } thead { display: table-header-group; } }
</style>
</head>
<body>
<h1>CueForge QC report</h1>
<p class="sub">${escapeHtml(model.sourceName)} · ${escapeHtml(model.presetName)} · ${escapeHtml(
    model.generatedAtDisplay,
  )}</p>
<table class="meta"><tbody>${meta}${referenceRow}</tbody></table>
<h2>Thresholds applied</h2>
<table><thead><tr><th>Rule</th><th>Limit</th><th>Severity</th></tr></thead><tbody>${thresholds}</tbody></table>
<h2>Issues</h2>
${emptyNote}
<table><thead><tr><th>Cue</th><th>Timecode</th><th>Rule</th><th>Severity</th><th>Message</th><th>Status</th><th>Auto-fixable</th></tr></thead><tbody>${rows}</tbody></table>
</body>
</html>`;
}

// ---------------------------------------------------------------------------
// Filename
// ---------------------------------------------------------------------------

/** `movie.srt` → `movie-qc.csv` / `movie-qc-report.html` (extension swapped). */
export function reportFilename(sourceName: string, kind: ReportKind): string {
  // Strip any path component and control chars so the derived name is a plain,
  // safe basename for the download attribute.
  // eslint-disable-next-line no-control-regex
  const safe = (sourceName.split(/[\\/]/).pop() ?? sourceName).replace(/[\u0000-\u001f]/g, '');
  const dot = safe.lastIndexOf('.');
  const base = dot > 0 ? safe.slice(0, dot) : safe;
  return kind === 'csv' ? `${base}-qc.csv` : `${base}-qc-report.html`;
}
